import { useCallback, useEffect, useRef, useState } from "react";
import { useUnmount } from "react-use";
import { LiveChatJoinResponse } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { StreamingHelper } from "@scrile/streaming-client";
import { TrackOptions } from "@scrile/streaming-client/build/ConsumerClient";
import { AudioActivityData } from "@scrile/streaming-client/build/AudioAnalyzer";
import useStreamHelper from "./useStreamHelper";
import { SendDataEvent } from "../const";
import { getScreenTracks, getVideoTracks, removeTracksFromStream } from "../lib/mediaStreamHelpers";
import providers from "../lib/providers";
import { ProduceUserData } from "../types";

interface ConsumeProps {
  token: string;
  joinData: LiveChatJoinResponse | null;
}

const MAX_CONSUME_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 2000;

export default function useConsumeController({ token, joinData }: ConsumeProps) {
  const { checkStreamForChanges } = useStreamHelper();
  const consumeUserIds = useRef<string[]>([]);
  const retryCounters = useRef<Record<string, number>>({});
  const retryTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [users, setUsers] = useState<Record<string, ProduceUserData>>({});
  const [isProducerFinishedStream, setIsProducerFinishedStream] = useState(false);
  // RC-4: a consume started under an older render closes over a stale joinData.
  // Read the latest roster through this ref in the async callbacks so a
  // late-resolving consume validates against the CURRENT roster instead of
  // silently dropping the stream attach against an outdated snapshot.
  const joinDataRef = useRef(joinData);
  joinDataRef.current = joinData;

  const updateUser = useCallback(
    (broadcasterChatUserId: string, { stream, ...data }: Partial<ProduceUserData>) => {
      const user = joinDataRef.current?.users.find((i) => i.id === broadcasterChatUserId);
      if (!user) return;
      setUsers((prevState) => {
        const localStream = prevState[broadcasterChatUserId]?.stream ?? new MediaStream([]);
        const localScreenStream = prevState[broadcasterChatUserId]?.screenStream ?? new MediaStream([]);
        const newStream = new MediaStream(stream ? [...getVideoTracks(stream), ...stream.getAudioTracks()] : []);
        const newScreenStream = new MediaStream(stream ? getScreenTracks(stream) : []);
        stream && localStream && checkStreamForChanges(newStream, localStream);
        stream && localScreenStream && checkStreamForChanges(newScreenStream, localScreenStream);
        return {
          ...prevState,
          [broadcasterChatUserId]: {
            ...prevState[broadcasterChatUserId],
            user,
            ...data,
            stream: localStream,
            screenStream: localScreenStream,
          },
        };
      });
    },
    [checkStreamForChanges]
  );

  const removeUser = useCallback(
    (broadcasterChatUserId: string) => {
      const user = users[broadcasterChatUserId];
      if (!user) return;

      user.WebRTC.closeAllStreams();
      user.stream && removeTracksFromStream(user.stream, user.stream.getTracks());
      user.screenStream && removeTracksFromStream(user.screenStream, user.screenStream.getTracks());

      setUsers((prevState) => {
        return Object.entries(prevState).reduce((acc, [id, user]) => {
          if (id !== broadcasterChatUserId) acc[id] = user;
          return acc;
        }, {} as Record<string, ProduceUserData>);
      });
      consumeUserIds.current = consumeUserIds.current.filter((i) => i !== broadcasterChatUserId);
    },
    [users]
  );

  const scheduleRetry = useCallback(
    (broadcasterChatUserId: string) => {
      const count = retryCounters.current[broadcasterChatUserId] ?? 0;
      if (count >= MAX_CONSUME_RETRIES) {
        // eslint-disable-next-line no-console
        console.warn(`[consume] Max retries (${MAX_CONSUME_RETRIES}) reached for user ${broadcasterChatUserId}`);
        delete retryCounters.current[broadcasterChatUserId];
        return;
      }
      retryCounters.current[broadcasterChatUserId] = count + 1;
      const delay = RETRY_BASE_DELAY_MS * Math.pow(2, count);
      // eslint-disable-next-line no-console
      console.info(`[consume] Retry ${count + 1}/${MAX_CONSUME_RETRIES} for ${broadcasterChatUserId} in ${delay}ms`);
      retryTimers.current[broadcasterChatUserId] = setTimeout(() => {
        delete retryTimers.current[broadcasterChatUserId];
        // Remove from consumed list so onConsumeStream can re-attempt
        consumeUserIds.current = consumeUserIds.current.filter((id) => id !== broadcasterChatUserId);
        // Re-trigger consume via the effect by checking if user is still broadcasting
        const user = joinDataRef.current?.users.find((i) => i.id === broadcasterChatUserId);
        if (user?.broadcasting) {
          onConsumeStream(broadcasterChatUserId);
        }
      }, delay);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Per-user data-channel messages from this producer. Defined before
  // onConsumeStream so it can be a stable dependency; only touches stable state
  // setters, so [] deps are correct.
  const onProducerData = useCallback(
    (broadcasterChatUserId: string, data: { event: SendDataEvent; muted?: boolean }) => {
      if (data.event === SendDataEvent.PRODUCER_FINISHED_STREAM) {
        setIsProducerFinishedStream(true);
      } else if (data.event === SendDataEvent.AUDIO_MUTED) {
        const muted = !!data.muted;
        setUsers((prev) => {
          const existing = prev[broadcasterChatUserId];
          if (!existing || existing.audioMuted === muted) return prev;
          return { ...prev, [broadcasterChatUserId]: { ...existing, audioMuted: muted } };
        });
      }
    },
    []
  );

  const onConsumeStream = useCallback(
    async (broadcasterChatUserId: string) => {
      setIsProducerFinishedStream(false);
      if (consumeUserIds.current.includes(broadcasterChatUserId)) return;
      consumeUserIds.current.push(broadcasterChatUserId);
      try {
        const connection = await providers.LivechatsProvider.watch({
          token,
          broadcasterChatUserId,
        });
        const WebRTC = new StreamingHelper();

        const stream = await WebRTC.consumeStream({
          connection: connection,
          tracks: {
            audio: false,
            video: false,
            screen: false,
          },
          onData: (data: { event: SendDataEvent; muted?: boolean }) => onProducerData(broadcasterChatUserId, data),
        });
        // Success — reset retry counter
        delete retryCounters.current[broadcasterChatUserId];
        updateUser(broadcasterChatUserId, { stream, connection, WebRTC });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`[consume] Failed to watch user ${broadcasterChatUserId}:`, e);
        consumeUserIds.current = consumeUserIds.current.filter((id) => id !== broadcasterChatUserId);
        scheduleRetry(broadcasterChatUserId);
      }
    },
    [token, updateUser, scheduleRetry, onProducerData]
  );

  const closeAllStream = () => {
    // Clear pending retry timers
    Object.values(retryTimers.current).forEach(clearTimeout);
    retryTimers.current = {};
    retryCounters.current = {};
    for (const userId of Object.keys(users)) {
      removeUser(userId);
    }
  };

  useEffect(() => {
    joinData &&
      joinData.users
        .filter((i) => i.id !== joinData.me.id)
        .forEach((i) => {
          if (i.broadcasting) {
            onConsumeStream(i.id);
          }
        });
  }, [joinData, onConsumeStream]);

  useEffect(() => {
    if (!joinData) return;
    const activeUserIds = joinData.users.filter((i) => i.id !== joinData.me.id).map((i) => i.id);
    consumeUserIds.current.forEach((id) => !activeUserIds.includes(id) && removeUser(id));
  }, [joinData, removeUser, users]);

  useEffect(() => {
    async function CB(data: Required<TrackOptions>, user: ProduceUserData) {
      const newMediaConstraints = { videoEnabled: data.video, audioEnabled: data.audio, shareEnabled: data.screen };
      const newStream = await user.WebRTC.consumeStream({
        connection: user.connection,
        tracks: { audio: data.audio, video: data.video, screen: data.screen },
      });
      updateUser(user.user.id, {
        stream: newStream,
        connection: user.connection,
        WebRTC: user.WebRTC,
        constrains: newMediaConstraints,
      });
    }
    const cbRecords: Record<string, typeof CB> = {};

    Object.values(users).forEach((user) => {
      const cb = (data: Required<TrackOptions>) => CB(data, user);
      cbRecords[user.user.id] = cb;
      user.WebRTC.on("producers-updated", cb);
    });
    return () => Object.values(users).forEach((user) => user.WebRTC.off("producers-updated", cbRecords[user.user.id]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  // Active-speaker detection. The remote producer's SDK runs an AudioAnalyzer
  // every 500 ms and broadcasts the result through the data channel; the
  // consumer-side StreamingHelper re-emits it as `audio-activity`. We mirror
  // it into the user's isSpeaking field — pure UI hint, completely separate
  // from the retry / removeUser flow so it can't break recording.
  //
  // Subscriptions are tracked in a ref keyed by userId so we attach exactly
  // ONCE per WebRTC instance and detach when the user leaves or their
  // WebRTC reference is swapped (e.g. after a reconnect/retry). If we used
  // `users` as a useEffect dep with inline attach+detach, our own setUsers
  // call inside the callback would cause an infinite resubscribe storm.
  const audioActivitySubs = useRef<
    Map<string, { webRTC: StreamingHelper; cb: (data: Partial<AudioActivityData>) => void }>
  >(new Map());
  useEffect(() => {
    const subs = audioActivitySubs.current;
    // 1. Detach for users that left OR whose WebRTC instance was replaced.
    Array.from(subs.entries()).forEach(([userId, sub]) => {
      const u = users[userId];
      if (!u || u.WebRTC !== sub.webRTC) {
        try {
          sub.webRTC.off("audio-activity", sub.cb);
        } catch {
          /* ignore */
        }
        subs.delete(userId);
      }
    });
    // 2. Attach for users newly present (or with a fresh WebRTC).
    Object.values(users).forEach((user) => {
      const userId = user.user.id;
      if (subs.has(userId)) return;
      const cb = (data: Partial<AudioActivityData>) => {
        const next = !!data?.isSpeaking;
        setUsers((prev) => {
          const existing = prev[userId];
          if (!existing || existing.isSpeaking === next) return prev;
          return { ...prev, [userId]: { ...existing, isSpeaking: next } };
        });
      };
      user.WebRTC.on("audio-activity", cb);
      subs.set(userId, { webRTC: user.WebRTC, cb });
    });
  }, [users]);

  // Detach every remaining audio-activity listener on unmount. Per-user
  // detach already happens above when a user is removed mid-call.
  useEffect(() => {
    const subs = audioActivitySubs.current;
    return () => {
      subs.forEach((sub) => {
        try {
          sub.webRTC.off("audio-activity", sub.cb);
        } catch {
          /* ignore */
        }
      });
      subs.clear();
    };
  }, []);

  useUnmount(closeAllStream);

  return {
    users,
    closeStream: closeAllStream,
    isProducerFinishedStream,
  };
}
